import {
  VERSION_NOTES_MAX_LENGTH,
  type Prototype,
  type UploadVersionResult,
} from '@atlas/api-contract';
import { Loader2, Upload } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import type { UploadProgress } from '@/lib/api/versions';
import { formatBytes } from '@/lib/format/bytes';
import { cn } from '@/lib/utils';

import { classifyUploadRejection, type UploadRejection } from './upload-errors';
import {
  ALLOWED_CONTENTS_SUMMARY,
  ARCHIVE_ACCEPT,
  precheckArchive,
  type UploadWarning,
} from './upload-precheck';
import { UploadRejectionNotice } from './upload-rejection-notice';
import { UploadResultView } from './upload-result';
import { useUploadVersion } from './versions-queries';

export interface UploadVersionDialogProps {
  /** The prototype being uploaded to, or `null` when the dialog is closed. */
  prototype: Prototype | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * What the dialog is doing right now.
 *
 * `uploading` and `processing` are deliberately separate. Once the last byte
 * has been sent the bar is at 100% and *nothing visible happens* while the
 * server opens the archive, checks four limits, vets every path and writes the
 * files — which on a large prototype is the longest part of the wait. A bar
 * parked at 100% reads as a hang, so the two phases say different things.
 */
type UploadPhase = 'choosing' | 'uploading' | 'processing' | 'done';

/**
 * Upload a ZIP as the next version of a prototype (spec R1.1–R1.10).
 *
 * Three decisions shape this dialog:
 *
 * - **An upload in flight cannot be dismissed by accident.** It is the one
 *   action in Atlas that takes minutes, and losing it to a stray click on the
 *   backdrop — after the bytes are already on the wire — would be the worst
 *   moment to lose it. Escape, the backdrop and the close button are all inert
 *   while it runs; cancelling is a deliberate, labelled button that aborts the
 *   request through an `AbortController` rather than abandoning it.
 * - **The pre-checks never block.** They are warnings beside the file input.
 *   The server owns the limits and has the only trustworthy view of the bytes,
 *   so a client-side rule that refused to send would eventually be wrong about
 *   a file the server would have accepted.
 * - **The dialog does not close on success.** The result — which version was
 *   created, and which files were dropped on the way — is the part worth
 *   reading, and it is only readable while the dialog is still open.
 */
export const UploadVersionDialog = ({
  prototype,
  onOpenChange,
}: UploadVersionDialogProps) => {
  const uploadVersion = useUploadVersion();

  const fileInputId = useId();
  const notesId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Whether the cancel happened after the last byte was sent.
   *
   * Aborting then only stops *waiting* for the answer — the server has the whole
   * archive and may well finish writing the version. Saying "nothing was saved"
   * in that case would be a guess, and the wrong one often enough to matter.
   */
  const cancelledWhileProcessingRef = useRef(false);

  const [file, setFile] = useState<File | null>(null);
  const [warnings, setWarnings] = useState<UploadWarning[]>([]);
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<UploadPhase>('choosing');
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [rejection, setRejection] = useState<UploadRejection | null>(null);
  const [result, setResult] = useState<UploadVersionResult | null>(null);

  const open = prototype !== null;
  const isBusy = phase === 'uploading' || phase === 'processing';

  /** Back to an empty picker, keeping the dialog open. */
  const reset = useCallback(() => {
    setFile(null);
    setWarnings([]);
    setNotes('');
    setPhase('choosing');
    setProgress(null);
    setRejection(null);
    setResult(null);

    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  /*
   * Abort anything still in flight if this unmounts — a navigation away, or the
   * prototype list refetching this card out of existence. Without it the
   * request would run to completion writing state into a component that is gone.
   */
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const handleFileChange = (chosen: File | null) => {
    setFile(chosen);
    setWarnings(chosen === null ? [] : precheckArchive(chosen));
    setRejection(null);
  };

  const handleUpload = async () => {
    if (prototype === null || file === null) {
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    cancelledWhileProcessingRef.current = false;

    setRejection(null);
    setProgress({ loaded: 0, total: file.size, percent: 0 });
    setPhase('uploading');

    try {
      const uploaded = await uploadVersion.mutateAsync({
        prototypeId: prototype.id,
        projectId: prototype.projectId,
        file,
        notes: notes.trim() === '' ? null : notes.trim(),
        signal: controller.signal,
        onProgress: (next) => {
          setProgress(next);
          setPhase(next.percent !== null && next.percent >= 100 ? 'processing' : 'uploading');
        },
      });

      setResult(uploaded);
      setPhase('done');
      toast.success(
        `Uploaded version ${String(uploaded.version.versionNumber)} of “${prototype.name}”.`,
      );
    } catch (error: unknown) {
      const classified = classifyUploadRejection(error);

      setPhase('choosing');
      setProgress(null);
      setRejection(
        classified.kind === 'cancelled' && cancelledWhileProcessingRef.current
          ? {
              ...classified,
              remedy:
                'The archive had already been sent, so the server may still have finished creating the version. Check the prototype before uploading it again.',
            }
          : classified,
      );
    } finally {
      abortRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    cancelledWhileProcessingRef.current = phase === 'processing';
    abortRef.current?.abort();
  };

  const percent = progress?.percent ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The one dismissal that has to be refused: closing mid-upload.
        if (!next && isBusy) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          'sm:max-w-xl',
          /*
           * The close button is a direct child of `DialogContent` (nothing else
           * in this dialog is), so while an upload runs it is dimmed to match
           * the `onOpenChange` guard above — a live-looking control that does
           * nothing is worse than one that says it is unavailable.
           */
          isBusy && '[&>button]:pointer-events-none [&>button]:opacity-30',
        )}
        {...(isBusy
          ? {
              onEscapeKeyDown: (event: KeyboardEvent) => {
                event.preventDefault();
              },
              onInteractOutside: (event: { preventDefault: () => void }) => {
                event.preventDefault();
              },
            }
          : {})}
      >
        <DialogHeader>
          <DialogTitle>
            {phase === 'done'
              ? `Uploaded to “${prototype?.name ?? ''}”`
              : `Upload a version of “${prototype?.name ?? ''}”`}
          </DialogTitle>
          <DialogDescription>
            {phase === 'done'
              ? 'The version is stored and immutable. Comments left on it stay with it.'
              : 'A ZIP containing index.html and the assets it loads. Each upload becomes the next numbered version.'}
          </DialogDescription>
        </DialogHeader>

        {result === null ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={fileInputId}>Archive</Label>
              <Input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                accept={ARCHIVE_ACCEPT}
                disabled={isBusy}
                onChange={(event) => {
                  handleFileChange(event.target.files?.[0] ?? null);
                }}
                className="h-auto cursor-pointer py-1.5 file:mr-3 file:cursor-pointer file:rounded-sm file:bg-secondary file:px-2 file:py-1"
              />
              {file === null ? (
                <p className="text-xs text-muted-foreground">
                  Kept when unpacked: {ALLOWED_CONTENTS_SUMMARY}. Anything else is
                  skipped and listed afterwards — never a reason to fail.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{file.name}</span> ·{' '}
                  {formatBytes(file.size)}
                </p>
              )}

              {warnings.map((warning) => (
                <p
                  key={warning.code}
                  className="text-xs text-amber-700 dark:text-amber-500"
                >
                  {warning.message}
                </p>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor={notesId}>Notes</Label>
              <Textarea
                id={notesId}
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
                disabled={isBusy}
                rows={3}
                maxLength={VERSION_NOTES_MAX_LENGTH}
                placeholder="What changed since the last version."
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Optional. Shown beside this version in the version picker.
              </p>
            </div>

            {isBusy ? (
              <div className="space-y-2" aria-live="polite">
                <Progress
                  value={phase === 'processing' ? null : percent}
                  label={
                    phase === 'processing'
                      ? 'Unpacking the archive'
                      : 'Uploading the archive'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {phase === 'processing'
                    ? 'Sent. Unpacking the archive and checking it — this can take a moment for a large prototype.'
                    : progress === null || progress.total === null
                      ? 'Uploading…'
                      : `Uploading ${formatBytes(progress.loaded)} of ${formatBytes(progress.total)} · ${String(percent ?? 0)}%`}
                </p>
              </div>
            ) : null}

            {rejection === null ? null : (
              <UploadRejectionNotice rejection={rejection} />
            )}
          </div>
        ) : (
          <UploadResultView
            result={result}
            onDone={() => {
              onOpenChange(false);
            }}
            onUploadAnother={reset}
          />
        )}

        {result === null ? (
          <DialogFooter className="gap-2 sm:items-center">
            {isBusy ? (
              <>
                <span className="flex items-center gap-2 text-xs text-muted-foreground sm:mr-auto">
                  <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                  Leaving this open keeps the upload alive.
                </span>
                <Button type="button" variant="outline" onClick={handleCancelUpload}>
                  {phase === 'processing' ? 'Stop waiting' : 'Cancel upload'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={file === null}
                  onClick={() => {
                    void handleUpload();
                  }}
                >
                  <Upload aria-hidden="true" />
                  {rejection?.canRetrySameFile === true ? 'Try again' : 'Upload version'}
                </Button>
              </>
            )}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
